/**
 * featureFlags.js — build-time switches for features that are built but not released.
 *
 * Pure module, no imports: the gates import it directly.
 *
 * ★★★ REPORT_PROBLEM_ENABLED (REP-10, v242.251.0) — ON since Alex's go of 2026-10-13 (00:1x): both routes are live
 *   in API v243.312.0. A Spanish patient reads the English copy until Dr. Rami's 59 es keys support.* / crash.*
 *   land (still PENDING_ES_WITHHELD; the screens are the same). The text below describes the OFF behaviour,
 *   which scripts/check-report-problem.mjs still measures with the flag stubbed false.
 *   Was: OFF, AND OFF MEANS UNREACHABLE.
 *   "Report a problem" in Settings and on the crash screen files a support ticket through
 *   POST /api/v1/support/tickets/patient and attaches files through
 *   POST /api/v1/support/tickets/patient/<ticket_id>/attachments. Both routes are built on the
 *   API's D7 branch and are NOT live, and the English copy is a draft Alex has not approved.
 *   With this false the app behaves exactly as v242.249.0: no Settings row, today's crash
 *   screen, no route memory, no request to either path. scripts/check-report-problem.mjs
 *   holds that (arm F) and, since the flip, fails if this line is anything but the literal `true` without the
 *   release conditions being met in the same commit.
 *
 *   FLIP ONLY WHEN ALL OF THESE ARE TRUE:
 *     1. the D7 API (both routes + the support_tickets.user_id and support_ticket_attachments
 *        DDL) is live in production;
 *     2. Alex approved the English copy listed in the REP-10 hand-off;
 *     3. (for Spanish) Dr. Rami approved the Spanish — until then every new key is
 *        PENDING_ES_WITHHELD and a Spanish-speaking patient keeps today's screens even with
 *        the flag on.
 */
export const REPORT_PROBLEM_ENABLED = true

/**
 * ★★★ STORE_UPDATE_PROMPT_ENABLED (PROD-257) — OFF. The iOS app asks the App Store whether a
 *   newer version is listed and, if so, shows the "A new version of Orella is available." bar
 *   (the approved updateNotice.* keys) with a button to the App Store page. Native only:
 *   src/main.jsx mounts StoreUpdateNotice under `!isWeb && STORE_UPDATE_PROMPT_ENABLED`, and the
 *   web keeps its own UpdateNotice (TEST 2.18) untouched. With this false nothing is mounted and
 *   no request is made. scripts/check-store-update.mjs holds both lines below to their literals.
 *
 *   FLIP ONLY WHEN ALL OF THESE ARE TRUE (Alex 2026-10-11 13:15: build now, flag off until the
 *   listing exists):
 *     1. the App Store listing is live;
 *     2. APP_STORE_ID below holds its numeric Apple ID (App Store Connect -> App Information);
 *     3. it was tested on a device build: the lookup answers through CapacitorHttp (CORS from
 *        capacitor://localhost is unverified) and the button opens the App Store app.
 */
export const STORE_UPDATE_PROMPT_ENABLED = false
export const APP_STORE_ID = ''

/**
 * s102 D7 (Dr. Rami list 3, 2026-10-13): the Observation disposition is TURNED OFF, not deleted.
 * The MD panel offers no Observation and the API refuses a new one; here, an outside visit note
 * the AI reads as "observation" is recorded as Closed. Every reader of an existing observation
 * state stays. The panel (src/config/observation.js) and the API (_OBSERVATION_ENABLED) carry
 * the same switch.
 */
export const OBSERVATION_ENABLED = false

/**
 * ★★★ INSURANCE_DISCOVERY_ENABLED (s110, Dr. Rami, TestFlight build 77, 2026-10-17) — OFF.
 *   "Removing the insurance discovery feature from the app. It's too costly and will eventually be a premium
 *   feature." TURNED OFF, not deleted: no "Search for this insurance" / "N of 3 searches left" (primary,
 *   secondary, onboarding), no automatic search in onboarding, no Home poll or 90-day re-check, and
 *   profileAPI.runInsuranceDiscovery answers { discovery: null, error: 'discovery_disabled' } without a request.
 *   The API carries the same switch (_INSURANCE_DISCOVERY_ENABLED + rcm_service.INSURANCE_DISCOVERY_ENABLED).
 *   Eligibility checks are unchanged. scripts/check-discovery-off.mjs holds it.
 */
export const INSURANCE_DISCOVERY_ENABLED = false

/**
 * ★★★ EPWORTH_ENABLED (s126, Dr. Rami 2026-10-31) — OFF. The Epworth Sleepiness Scale is licensed (its copyright holder's
 *   site: "A license is required to use these questionnaires"); his ruling: only free or approved scales are asked in the
 *   app, so Epworth is TURNED OFF, not deleted (the s102 rule). With this false the questionnaire store drops every
 *   question of type 'epworth' from the catalog it serves (bundled AND live), so Describe never asks it, never counts it
 *   and never offers it to the voice-memo fill. Readers stay: EpworthScale.jsx, the saved answers on existing cases, the
 *   panel's display of them. It returns as an in-office recommendation for the clinician (canvas "Orella Diagnostic
 *   Scales" C). Flip ONLY with a licence in writing. scripts/check-s126-fixes.mjs holds the literal.
 */
export const EPWORTH_ENABLED = false
export const RETIRED_QUESTION_TYPES = EPWORTH_ENABLED ? [] : ['epworth']
